/**
 * SAFE IMAGE COMPONENT
 * Uses REACT_APP_API_URL for image base so it works in local and production.
 */

import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';

const SafeImage = ({ 
  src, 
  alt, 
  className = '', 
  fallbackSrc = null,
  placeholder = null,
  onLoad = null,
  onError = null,
  ...props 
}) => {
  const [imageSrc, setImageSrc] = useState(src);
  const [isLoading, setIsLoading] = useState(true);

  // Default placeholder SVG
  const defaultPlaceholder = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMzAwIiBoZWlnaHQ9IjMwMCIgZmlsbD0iI2Y3ZjdmNyIvPgogIDx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5JbWFnZSBOb3QgRm91bmQ8L3RleHQ+Cjwvc3ZnPgo=';

  // Reset state when src changes
  useEffect(() => {
    setImageSrc(fixImageUrl(src));
    setIsLoading(true);
  }, [src]);

  // Fix image URL to ensure it's properly formatted and encoded
  const fixImageUrl = (url) => {
    if (!url) return defaultPlaceholder;
    
    // If it's already a data URL, return as is
    if (url.startsWith('data:')) {
      return url;
    }
    
    let finalUrl = url;
    
    if (url.startsWith('http://') || url.startsWith('https://')) {
      finalUrl = url;
    } else if (url.startsWith('/')) {
      finalUrl = `${API_BASE_URL}${url}`;
    } else {
      finalUrl = `${API_BASE_URL}/images/handbags/${url}`;
    }
    
    // Encode filename for spaces/special chars (relative or absolute URL)
    try {
      const base = typeof window !== 'undefined' ? window.location.origin : '';
      const urlObj = new URL(finalUrl.startsWith('http') ? finalUrl : finalUrl.replace(/^\//, base + '/'));
      const pathParts = urlObj.pathname.split('/').filter(Boolean);
      const filename = pathParts[pathParts.length - 1];
      if (filename && (filename.includes(' ') || filename.includes('%'))) {
        pathParts[pathParts.length - 1] = encodeURIComponent(decodeURIComponent(filename));
        const encoded = '/' + pathParts.join('/');
        return finalUrl.startsWith('http') ? urlObj.origin + encoded : encoded;
      }
    } catch (e) {
      console.warn('Failed to parse URL for encoding:', finalUrl, e);
    }
    return finalUrl;
  };

  const handleLoad = (event) => {
    setIsLoading(false);
    if (onLoad) onLoad(event);
  };

  const handleError = (event) => {
    console.warn(`Failed to load image: ${imageSrc}`);
    setIsLoading(false);
    
    // Try fallback image if provided and not already tried
    if (fallbackSrc && imageSrc !== fixImageUrl(fallbackSrc)) {
      setImageSrc(fixImageUrl(fallbackSrc));
      return;
    }
    
    // Use placeholder as final fallback
    if (imageSrc !== defaultPlaceholder) {
      setImageSrc(placeholder || defaultPlaceholder);
      return;
    }
    
    if (onError) onError(event);
  };

  const finalSrc = fixImageUrl(imageSrc);

  return (
    <img
      src={finalSrc}
      alt={alt}
      className={`${className} ${isLoading ? 'opacity-50' : 'opacity-100'} transition-opacity duration-300`}
      onLoad={handleLoad}
      onError={handleError}
      {...props}
    />
  );
};

export default SafeImage;
